import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { DataModalOptions } from '../../../../models/base-modal-component';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DataArea } from '../../../../models/data-area';
import { DeferredPromise } from '../../../../models/deferred-promise';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { ConnectorType, OperationType, DataAreaType, Endpoint, HelpIndex, MessageType, UpdateStatusType, ReviewSystem } from '../../../../models/enums';
import { CellTemplateType, GenericColDef } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DataAreaModalComponent } from '../../data-area-modal/data-area-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';

enum MenuItem {
    VALIDATE,
    EDIT,
    DELETE
}

@Component({
    standalone: false,
    selector: 'res-data-areas',
    templateUrl: './data-areas.component.html',
    styleUrls: ['./data-areas.component.scss']
})
export class DataAreasSettingsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;
    dataAreaType: DataAreaType;
    isBusy = false;
    isBusyMessage = '';

    gridName = 'Data Areas';
    automationId = 'data-areas';
    helpIndex: HelpIndex;
    updateStatusType = UpdateStatusType.DATA_AREA;

    // permissions
    permissionType: ObjectActionListType;
    hasDAViewOnlyPermission: boolean;
    hasDACreatePermission: boolean;
    hasDADeletePermission: boolean;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Data Area Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Connector Name',
            field: 'connectorName',
            automationId: this.automationId
        },
        {
            headerName: 'Type',
            field: 'connectorType',
            automationId: this.automationId,
            minWidth: 110, maxWidth: 110, width: 110,
            suppressSizeToFit: true
        },
        {
            headerName: 'Mode',
            field: 'mode',
            automationId: this.automationId,
            minWidth: 95, maxWidth: 95, width: 95,
            suppressSizeToFit: true
        },
        {
            headerName: 'Server',
            field: 'server',
            automationId: this.automationId
        },
        {
            headerName: 'Connector Path',
            field: 'remotePath',
            automationId: this.automationId
        },
        {
            headerName: 'Data Area Path',
            field: 'path',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );
    requestInfo: DrRequestInfo;
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Validate', iconClass: 'fa-check', iconColor: 'color-blue-01',
            click: (item: DataArea) => this.contextMenuActions(MenuItem.VALIDATE, item)
        }),
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
            click: (item: DataArea) => this.contextMenuActions(MenuItem.EDIT, item)
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
            click: (item: DataArea) => this.contextMenuActions(MenuItem.DELETE, item),
            enabled: (item: any) => item.connectorType !== ReviewSystem.REEF_REVIEW
        })
    ];

    rowClassRules = {
        'disabled-row': () => {
            return this.hasDAViewOnlyPermission;
        }
    };

    responseArrayCallback = (value: any): Promise<any> => {
        // More data is required to fill the columns because
        // the original data areas endpoint does not have some of the connector information (such as name).
        // We fill that in using another post call, so we need to return a promise for that
        // call instead of the data itself.  Otherwise the data would be returned after
        // empty values had been inserted into the grid and the data would not be displayed.
        const request = new DeferredPromise<any>();

        this.getConnector(value.connectorHandle).then((connResponse) => {
            value.onContextMenuAction = this.contextMenuActions;
            value.connectorName = connResponse.name;
            value.mode = connResponse.mode === 'READWRITE' ? 'Read / Write' : 'Read-Only';
            value.server = connResponse.networkId;
            value.connectorType = connResponse.type;
            value.remotePath = connResponse.remotePath;
            request.resolve(new DataArea(value));
        }).catch((response) => request.resolve(new DataArea(value)));

        return request.promise;
    }

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof DataArea) {
            this.contextMenuActions(MenuItem.EDIT, rowNode.data);
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) {
    }

    selectedTreeItemChanged = (node: GenericTreeNode, projectHandle?: string) => {
        this.dataAreaType = node.bundle as DataAreaType;
        this.setHelpIndex();
        switch (this.dataAreaType) {
            case DataAreaType.PROJECT: {
                this.requestInfo = Object.assign({}, new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listProjectDataAreas,
                    data: {},
                    responseName: 'projectDataAreas'
                }));
                this.permissionType = ObjectActionListType.PROJECT_DATA_AREA;
                break;
            }
            case DataAreaType.EXPORT: {
                this.requestInfo = Object.assign({}, new DrRequestInfo({
                    endpoint: Endpoint.orgManager_listExportDataAreas,
                    data: {},
                    responseName: 'exportDataAreas'
                }));
                this.permissionType = ObjectActionListType.EXPORT_DATA_AREA;
                break;
            }
        }

        this.hasDACreatePermission = Permissions.checkUserPermissions([{ permissionType: this.permissionType, permissionActions: [ActionType.CREATE] }]);
        this.hasDAViewOnlyPermission = Permissions.checkUserPermission(this.permissionType) && !this.hasDACreatePermission;
        this.hasDADeletePermission = Permissions.checkUserPermissions([{ permissionType: this.permissionType, permissionActions: [ActionType.DELETE] }]);

        this.contextMenuOptions[1].permissions = [{ permissionType: this.permissionType, permissionActions: [ActionType.CREATE] }];
        this.contextMenuOptions[2].permissions = [{ permissionType: this.permissionType, permissionActions: [ActionType.DELETE] }];

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasDAViewOnlyPermission) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }

        this.genericGrid.loadData();
    }

    setHelpIndex(): void {
        this.helpIndex = this.dataAreaType === DataAreaType.PROJECT ? HelpIndex.project_data_areas_org_manage_dlg : HelpIndex.export_data_areas_org_manage_dlg;
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    contextMenuActions(actionType: MenuItem, dataArea: DataArea): void {
        if (dataArea) {
            switch (actionType) {
                case MenuItem.VALIDATE: {
                    this.validateDataAreaModal(dataArea);
                    break;
                }
                case MenuItem.EDIT: {
                    this.editDataAreaModal(dataArea);
                    break;
                }
                case MenuItem.DELETE: {
                    this.deleteDataAreaModel(dataArea);
                    break;
                }
            }
        }
    }

    getConnector(handle: string): Promise<any> {
        const request = new DeferredPromise<any>();

        this.restService.post(Endpoint.connectorManager_getConnector, { handle: handle }).then((response) => {
            request.resolve(response.connector);
        }).catch((response) => {
            request.reject(response);
        });

        return request.promise;
    }

    openNewDataAreaModal(): void {
        this.commonService.openModal(DataAreaModalComponent,
            new DataModalOptions({
                backdrop: false, class: 'vertical-percent-100 horizontal-percent-100', modalData: {
                    operationType: OperationType.new,
                    dataAreaType: this.dataAreaType
                }
            })).then((value) => {
                if (value.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    validateDataAreaModal(item): void {
        this.isBusyMessage = 'Validating ' + item.connectorMode + 'Data Area...';
        const endpoint = Endpoint.dataAreaManager_validateDataArea;
        const request = {
            description: item.description,
            name: item.connectorName,
            connectorHandle: item.connectorHandle,
            handle: item.handle,
            edit: true,
            errorIfExists: true,
            force: false,
            mode: item.connectorMode,
            path: item.path
        };
        this.restService.post(endpoint, request, false).then(response => {
            this.isBusy = false;
            if (response && response.status === 'SUCCESS') {
                this.commonService.openSuccessToastNotification('The Data Area is valid.');
            }
        }, error => {
            this.isBusy = false;
            this.commonService.openErrorModal('Data Area directory path does not exist.', 'Error');
        });
    }

    editDataAreaModal(item): void {
        if (item?.connectorType === ReviewSystem.REEF_REVIEW) {
            this.commonService.openModal(CreateEditEntityModalComponent, { modalData: item }).then((value) => {
                if (value.modalResult) {
                    this.genericGrid.loadData();
                }
            });
        } else {
            this.commonService.openModal(DataAreaModalComponent,
                new DataModalOptions({
                    backdrop: false, class: 'vertical-percent-100 horizontal-percent-100', modalData: {
                        operationType: OperationType.edit,
                        dataAreaType: this.dataAreaType, dataAreaItem: item
                    }
                })).then((value) => {
                    if (value.modalResult) {
                        this.genericGrid.loadData();
                    }
                });
        }
    }

    deleteDataAreaModel(dataArea): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: dataArea });
    }
}
